import { WGSL_LAYOUT } from "../../../compute/compute_layout_constants";
import { readBlob2At } from "../../../compute/blob_helpers";
import {
  readStickerAt,
  STICKERINFOGPU_STRIDE_BYTES,
  type StickerInfoGpu,
} from "../../../compute/stickergpu_helpers";
import { u64ToLowHighWords } from "../../../compute/sticker_code64";

const STICKER_TYPE_3X3 = 3;

const COLOR_INDEX_BLUE = 0;
const COLOR_INDEX_GREEN = 1;
const COLOR_INDEX_RED = 2;
const LOW_COLOR_SLOT = 4;
const HIGH_COLOR_SLOT = 2;
const FIXED_RED_SLOT = 0;
const OPPOSITE_TOP_SLOT = 6;

const CANONICAL_GRID_SLOTS = [0, 1, 2, 7, 8, 3, 6, 5, 4] as const;
const COLOR_TRIT_SLOTS = [5, 3, 8, 7, 1] as const;
const SPIN_DIGIT_SLOTS = [4, 5, 6, 3, 8, 7, 2, 1, 0] as const;

const BLOB2_STRIDE_BYTES = WGSL_LAYOUT.Blob2.size;
const RAW_SPIN_RECOVERY_OFFSET = 3;
const RELATED_SHARED_MIN = 5;
const MAX_RELATED = 6;

const COLOR_TRIT_POSITION = new Map<number, number>(
  COLOR_TRIT_SLOTS.map((slot, index) => [slot, index])
);
const SPIN_DIGIT_POSITION = new Map<number, number>(
  SPIN_DIGIT_SLOTS.map((slot, index) => [slot, index])
);

type BlobSnapshot = ReturnType<typeof readBlob2At>;
type StickerCandidate = {
  index: number;
  sticker: StickerInfoGpu;
  blobs: number[];
  blobKey: string;
};

type RelatedCandidate = StickerCandidate & {
  sharedCount: number;
  sameSet: boolean;
};

export type Decoded3x3Code = {
  lowBit: number;
  colorTrits: number[];
  spinDigits: number[];
  topBit: number;
  colorsBySlot: number[];
  spinsBySlot: number[];
  canonicalColors: number[];
  canonicalSpins: number[];
};

function formatNumber(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return "nan";
  const fixed = value.toFixed(digits);
  return fixed
    .replace(/\.00$/, "")
    .replace(/(\.\d*[1-9])0+$/, "$1")
    .replace(/^-0(?:\.0+)?$/, "0");
}

function formatVec2(x: number, y: number): string {
  return `${formatNumber(x)},${formatNumber(y)}`;
}

function formatWordHex(word: number): string {
  return `0x${(word >>> 0).toString(16).padStart(8, "0")}`;
}

function colorToken(colorIndex: number): string {
  switch (colorIndex) {
    case COLOR_INDEX_BLUE:
      return "B0";
    case COLOR_INDEX_GREEN:
      return "G1";
    case COLOR_INDEX_RED:
      return "R2";
    case 3:
      return "K3";
    default:
      return `U${colorIndex}`;
  }
}

function recoverRawSpin(packedSpin: number): number {
  return (packedSpin + RAW_SPIN_RECOVERY_OFFSET) & 7;
}

function stickerCount(words: Uint32Array): number {
  return Math.floor(words.byteLength / STICKERINFOGPU_STRIDE_BYTES);
}

function blobCount(words: Uint32Array): number {
  return Math.floor(words.byteLength / BLOB2_STRIDE_BYTES);
}

function takeStickerBlobs(sticker: StickerInfoGpu): number[] {
  return sticker.blob_list.slice(0, 9).map((value) => value | 0);
}

function blobKey(blobs: number[]): string {
  return [...blobs]
    .filter((value) => value >= 0)
    .sort((a, b) => a - b)
    .join(",");
}

function sharedBlobCount(left: number[], right: number[]): number {
  const rightSet = new Set(right.filter((value) => value >= 0));
  let shared = 0;
  for (const value of new Set(left.filter((entry) => entry >= 0))) {
    if (rightSet.has(value)) shared++;
  }
  return shared;
}

function readCandidates(words: Uint32Array): StickerCandidate[] {
  const count = stickerCount(words);
  const out: StickerCandidate[] = [];

  for (let index = 0; index < count; index++) {
    const sticker = readStickerAt(words, index);
    if (sticker.xtypex !== STICKER_TYPE_3X3) continue;
    const blobs = takeStickerBlobs(sticker);
    out.push({
      index,
      sticker,
      blobs,
      blobKey: blobKey(blobs),
    });
  }

  return out;
}

function readBlobMap(words: Uint32Array, indices: number[]): Map<number, BlobSnapshot> {
  const out = new Map<number, BlobSnapshot>();
  const max = blobCount(words);

  for (const index of indices) {
    if (index < 0 || index >= max || out.has(index)) continue;
    out.set(index, readBlob2At(words, index));
  }

  return out;
}

function pickFocusCandidate(
  candidates: StickerCandidate[],
  triggerIndex: number,
  acceptedCode: bigint
): StickerCandidate | null {
  const trigger = candidates.find((candidate) => candidate.index === triggerIndex) ?? null;
  if (trigger !== null && trigger.sticker.code === acceptedCode) {
    return trigger;
  }

  const accepted = candidates.find((candidate) => candidate.sticker.code === acceptedCode) ?? null;
  if (accepted !== null) {
    return accepted;
  }

  return trigger;
}

function deriveTopBit(sticker: StickerInfoGpu, blobs: Map<number, BlobSnapshot>): number {
  const slot2BlobIndex = sticker.blob_list[2];
  const slot2Blob = blobs.get(slot2BlobIndex);
  if (slot2Blob) {
    return slot2Blob.color_index === COLOR_INDEX_BLUE ? 1 : 0;
  }
  return sticker.circle_colors[2] === COLOR_INDEX_BLUE ? 1 : 0;
}

function formatPackCheck(packed: bigint, repacked: bigint): string {
  return packed === repacked ? "ok" : repacked.toString();
}

function formatFailBlob(failBlob: number): string {
  return failBlob >= 0 ? `b${failBlob}` : "-";
}

function formatRelatedCandidate(
  candidate: RelatedCandidate,
  blobs: Map<number, BlobSnapshot>,
  focusIndex: number
): string {
  const anchorBlob = candidate.sticker.blob_list[0];
  const topBit = deriveTopBit(candidate.sticker, blobs);
  const focusMark = candidate.index === focusIndex ? "*" : "";
  const shared = candidate.sameSet ? "same9" : `sh${candidate.sharedCount}`;
  return `#${candidate.index}/b${anchorBlob} ${candidate.sticker.code.toString()}${focusMark} ${shared} t${topBit} u=${formatVec2(candidate.sticker.sticker_direction[0], candidate.sticker.sticker_direction[1])}`;
}

function buildRelatedCandidates(
  candidates: StickerCandidate[],
  focus: StickerCandidate,
  blobs: Map<number, BlobSnapshot>
): string {
  const related: RelatedCandidate[] = candidates
    .filter((candidate) => candidate.index !== focus.index)
    .map((candidate) => ({
      ...candidate,
      sharedCount: sharedBlobCount(candidate.blobs, focus.blobs),
      sameSet: candidate.blobKey === focus.blobKey,
    }))
    .filter((candidate) => candidate.sameSet || candidate.sharedCount >= RELATED_SHARED_MIN)
    .sort((left, right) => {
      if (Number(right.sameSet) !== Number(left.sameSet)) {
        return Number(right.sameSet) - Number(left.sameSet);
      }
      if (right.sharedCount !== left.sharedCount) {
        return right.sharedCount - left.sharedCount;
      }
      if (left.sticker.code !== right.sticker.code) {
        return left.sticker.code < right.sticker.code ? -1 : 1;
      }
      return left.index - right.index;
    });

  const shown = [
    {
      ...focus,
      sharedCount: 9,
      sameSet: true,
    } satisfies RelatedCandidate,
    ...related.slice(0, MAX_RELATED - 1),
  ].map((candidate) => formatRelatedCandidate(candidate, blobs, focus.index));

  return `cands=[${shown.join(" | ")}]`;
}

function cellContribution(slot: number, packedColor: number, topBit: number): string {
  if (slot === FIXED_RED_SLOT) {
    return "fixed=R";
  }

  if (slot === LOW_COLOR_SLOT) {
    return `lo=${packedColor === COLOR_INDEX_GREEN ? 1 : 0}`;
  }

  if (slot === HIGH_COLOR_SLOT || slot === OPPOSITE_TOP_SLOT) {
    return `top=${topBit}`;
  }

  const colorPos = COLOR_TRIT_POSITION.get(slot);
  if (colorPos !== undefined) {
    return `ct${colorPos}=${packedColor}`;
  }

  return "";
}

export function pack3x3CodeFromCandidate(
  circleColors: number[],
  circleSpins: number[],
  topBit: number
): bigint {
  if (circleColors[LOW_COLOR_SLOT] === COLOR_INDEX_RED) {
    return 0n;
  }

  let code = 0n;
  let base = 1n;

  if (circleColors[LOW_COLOR_SLOT] === COLOR_INDEX_GREEN) {
    code += base;
  }
  base *= 2n;

  for (const slot of COLOR_TRIT_SLOTS) {
    code += base * BigInt(Math.max(circleColors[slot], 0));
    base *= 3n;
  }

  for (let index = 0; index < SPIN_DIGIT_SLOTS.length; index++) {
    const slot = SPIN_DIGIT_SLOTS[index];
    code += base * BigInt(circleSpins[slot] & 7);
    if (index < SPIN_DIGIT_SLOTS.length - 1) {
      base *= 8n;
    }
  }

  if (topBit !== 0) {
    base *= 8n;
    code += base;
  }

  return code;
}

export function decodePacked3x3Code(code: bigint): Decoded3x3Code {
  let remaining = BigInt.asUintN(64, code);

  const lowBit = Number(remaining & 1n);
  remaining >>= 1n;

  const colorTrits = new Array<number>(COLOR_TRIT_SLOTS.length).fill(0);
  for (let index = 0; index < COLOR_TRIT_SLOTS.length; index++) {
    colorTrits[index] = Number(remaining % 3n);
    remaining /= 3n;
  }

  const spinDigits = new Array<number>(SPIN_DIGIT_SLOTS.length).fill(0);
  for (let index = 0; index < SPIN_DIGIT_SLOTS.length; index++) {
    spinDigits[index] = Number(remaining % 8n);
    remaining /= 8n;
  }

  const topBit = Number(remaining & 1n);

  const colorsBySlot = new Array<number>(9).fill(-1);
  colorsBySlot[FIXED_RED_SLOT] = COLOR_INDEX_RED;
  colorsBySlot[HIGH_COLOR_SLOT] = topBit === 1 ? COLOR_INDEX_BLUE : COLOR_INDEX_GREEN;
  colorsBySlot[LOW_COLOR_SLOT] = lowBit === 1 ? COLOR_INDEX_GREEN : COLOR_INDEX_BLUE;
  colorsBySlot[OPPOSITE_TOP_SLOT] = topBit === 1 ? COLOR_INDEX_GREEN : COLOR_INDEX_BLUE;
  COLOR_TRIT_SLOTS.forEach((slot, index) => {
    colorsBySlot[slot] = colorTrits[index];
  });

  const spinsBySlot = new Array<number>(9).fill(0);
  SPIN_DIGIT_SLOTS.forEach((slot, index) => {
    spinsBySlot[slot] = spinDigits[index];
  });

  return {
    lowBit,
    colorTrits,
    spinDigits,
    topBit,
    colorsBySlot,
    spinsBySlot,
    canonicalColors: CANONICAL_GRID_SLOTS.map((slot) => colorsBySlot[slot]),
    canonicalSpins: CANONICAL_GRID_SLOTS.map((slot) => spinsBySlot[slot]),
  };
}

export function buildAcceptedStickerDiagnosticLog(args: {
  acceptedCode: bigint;
  triggerCode: bigint;
  triggerIndex: number;
  reason: string;
  stickerWords: Uint32Array;
  blobWords: Uint32Array;
}): string | null {
  const candidates = readCandidates(args.stickerWords);
  const focus = pickFocusCandidate(candidates, args.triggerIndex, args.acceptedCode);
  if (focus === null) {
    return null;
  }

  const relatedBlobIndices = new Set<number>(focus.blobs);
  for (const candidate of candidates) {
    if (candidate.index === focus.index) continue;
    const shared = sharedBlobCount(candidate.blobs, focus.blobs);
    const sameSet = candidate.blobKey === focus.blobKey;
    if (!sameSet && shared < RELATED_SHARED_MIN) continue;
    for (const blobIndex of candidate.blobs) relatedBlobIndices.add(blobIndex);
  }

  const blobMap = readBlobMap(args.blobWords, [...relatedBlobIndices]);
  const topBit = deriveTopBit(focus.sticker, blobMap);
  const words = u64ToLowHighWords(focus.sticker.code);
  const repacked = pack3x3CodeFromCandidate(
    focus.sticker.circle_colors,
    focus.sticker.circle_spins,
    topBit
  );

  const focusRole = focus.index === args.triggerIndex ? "trigger" : "accepted";
  const header = [
    `[scan3x3] accept=${args.acceptedCode.toString()}`,
    `trigger=#${args.triggerIndex}:${args.triggerCode.toString()}`,
    `why=${args.reason}`,
    `focus=#${focus.index}:${focus.sticker.code.toString()}(${focusRole})`,
  ].join(" ");

  const meta = [
    `u64 lo=${formatWordHex(words.low)}`,
    `hi=${formatWordHex(words.high)}`,
    `dec=${focus.sticker.code.toString()}`,
    `repack=${formatPackCheck(focus.sticker.code, repacked)}`,
    `stage=${focus.sticker.debug_stage}`,
    `fail=${formatFailBlob(focus.sticker.debug_fail_blob)}`,
  ].join(" ");

  const topBlobIndex = focus.sticker.blob_list[2];
  const topBlob = blobMap.get(topBlobIndex);
  const traversal = focus.blobs.join(",");
  const route = [
    `anchor=s0:b${focus.sticker.blob_list[0]}`,
    `path=[${traversal}]`,
    `dir=${formatVec2(focus.sticker.sticker_direction[0], focus.sticker.sticker_direction[1])}`,
    `top=${topBit} via s2=b${topBlobIndex}/${colorToken(topBlob?.color_index ?? focus.sticker.circle_colors[HIGH_COLOR_SLOT])}`,
  ].join(" ");

  const lines = [
    header,
    meta,
    route,
    buildRelatedCandidates(candidates, focus, blobMap),
    "cells rc s b xy raw/pack m sp(raw/cur->pk) sd bits",
  ];

  for (let canonicalIndex = 0; canonicalIndex < CANONICAL_GRID_SLOTS.length; canonicalIndex++) {
    const slot = CANONICAL_GRID_SLOTS[canonicalIndex];
    const row = Math.floor(canonicalIndex / 3);
    const col = canonicalIndex % 3;
    const blobIndex = focus.sticker.blob_list[slot];
    const blob = blobMap.get(blobIndex);
    const packedColor = focus.sticker.circle_colors[slot];
    const packedSpin = focus.sticker.circle_spins[slot] & 7;
    const rawSpin = recoverRawSpin(packedSpin);
    const currentSpin = blob ? blob.spin_debug.spin : -1;
    const spinDigit = SPIN_DIGIT_POSITION.get(slot) ?? -1;
    const bits = cellContribution(slot, packedColor, topBit);

    lines.push(
      [
        `r${row}c${col}`,
        `s${slot}`,
        `b${blobIndex}`,
        blob ? `${blob.x},${blob.y}` : "?,?",
        `${colorToken(blob?.color_index ?? packedColor)}/${packedColor}`,
        `m${blob?.black_marker ?? -1}`,
        `sp${rawSpin}/${currentSpin}->${packedSpin}`,
        spinDigit >= 0 ? `sd${spinDigit}` : "sd-",
        bits,
      ]
        .filter((part) => part.length > 0)
        .join(" ")
    );
  }

  return lines.join("\n");
}
